import { useRef, type ReactNode } from "react"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Upload } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useUploadStore } from "@/stores/upload"
import { useAuthStore } from "@/stores/auth"

interface BreadcrumbItem {
  label: string
  href?: string
}

interface PageHeaderProps {
  title?: string
  description?: string
  breadcrumbs?: BreadcrumbItem[]
  actions?: ReactNode
  uploadAssetId?: number
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  uploadAssetId,
}: PageHeaderProps) {
  const user = useAuthStore(s => s.user)
  const { uploadFiles, uploadFilesToAsset } = useUploadStore()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const canUpload = user?.role === 'Admin' || user?.role === 'Editor' || user?.role === 'Photographer'

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    if (uploadAssetId) {
      uploadFilesToAsset(uploadAssetId, files)
    } else {
      uploadFiles(files)
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }
  return (
    <header className="sticky top-0 z-10 border-b bg-background">
      {/* Top bar with breadcrumbs and actions */}
      <div className="flex h-16 items-center gap-4 px-6">
        <SidebarTrigger className="lg:hidden" />

        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            {breadcrumbs.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                {i > 0 && <span>/</span>}
                {item.href ? (
                  <a
                    href={item.href}
                    className="hover:text-foreground transition-colors"
                  >
                    {item.label}
                  </a>
                ) : (
                  <span className="text-foreground font-medium">
                    {item.label}
                  </span>
                )}
              </div>
            ))}
          </nav>
        )}

        <div className="ml-auto flex items-center gap-2">
          {canUpload && (
            <>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4" />
                Upload
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
            </>
          )}
          {actions}
        </div>
      </div>

      {/* Title section (optional) */}
      {(title || description) && (
        <div className="border-t px-6 py-6">
          {title && (
            <h1 className="scroll-m-20 text-3xl/tight font-bold tracking-tight">
              {title}
            </h1>
          )}
          {description && (
            <p className="text-muted-foreground mt-2">
              {description}
            </p>
          )}
        </div>
      )}
    </header>
  )
}
